import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as BellIcon } from "../../assets/icons/bell.svg";
import { ReactComponent as ArrowIcon } from "../../assets/icons/arrow.svg";
import { ReactComponent as Triangle } from "../../assets/icons/triangle.svg";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../app/api/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RootState, AppDispatch } from "../../app/store";
import { clearUser } from "../../app/slices/userSlice";
import {
  capitalizeFirstLetter,
  stringShortener,
} from "../../utils/commonUtils";
import { useNotifications } from "../../app/hooks/GetNotication";
import { INotification } from "../../interface/INotification";
import NotificationModal from "../Notification/NotificationModal";
import { AxiosError } from "axios";
import { ReactComponent as ReturnArrow } from "../../assets/icons/returnArrow.svg";
import {
  CLIENT_SSO_ENTRY_PATH,
  getClientPlatformReturnUrl,
  isClientSsoUser,
  markClientSsoSessionEnded,
  redirectToClientPlatform,
} from "../../constants/clientSso";
import { useImageGeneratorJobsOptional } from "../../pages/GenerativePlatform/ImageGeneratorJobsContext";
import { useCollectionBuildSessionsOptional } from "../../pages/GenerativePlatform/CollectionBuildSessionsContext";

function getUserInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.toUpperCase().split(" ").filter(Boolean);
  if (parts.length === 1) {
    return parts[0].charAt(0);
  } else if (parts.length === 2) {
    return parts.map(n => n.charAt(0)).join("");
  } else if (parts.length > 2) {
    return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
  } else {
    return "?";
  }
}

const TopNav = () => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isNotificationOpen, setNotificationOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState<
    INotification[]
  >([]);
  const notificationRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellIconRef = useRef<HTMLDivElement>(null);
  const logoutDropdownArrowRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user.userInfo);
  const notifications = useNotifications();
  const imageJobs = useImageGeneratorJobsOptional();
  const collectionJobs = useCollectionBuildSessionsOptional();

  const { name, group } = user || {};
  const isClientUser = isClientSsoUser(group);
  const showPlatformNotifications = !isClientUser;
  const showImageGeneratorBell = Boolean(
    isClientUser && (imageJobs || collectionJobs)
  );
  const generationUnread =
    (imageJobs?.unreadCount || 0) + (collectionJobs?.unreadCount || 0);
  const generationNotifications = [
    ...(imageJobs?.notifications || []).map(item => ({
      ...item,
      source: "image" as const,
    })),
    ...(collectionJobs?.notifications || []).map(item => ({
      ...item,
      source: "collection" as const,
      errorMessage: undefined as string | undefined,
    })),
  ].sort(
    (left, right) => new Date(right.at).getTime() - new Date(left.at).getTime()
  );
  const clientReturnUrl = getClientPlatformReturnUrl();

  const handleBackToPlatform = () => {
    redirectToClientPlatform();
  };

  const handleLogout = async () => {
    const wasClient = isClientSsoUser(group);
    try {
      await dispatch(logout());
      dispatch(clearUser());
      sessionStorage.clear();
      if (wasClient) {
        if (clientReturnUrl) {
          window.location.assign(clientReturnUrl);
          return;
        }
        markClientSsoSessionEnded(
          "You have been signed out. Please return to the client platform."
        );
        navigate(CLIENT_SSO_ENTRY_PATH, { replace: true });
        return;
      }
      navigate("/auth/login");
    } catch (error) {
      if ((error as AxiosError).response?.status !== 400)
        toast.error("Error logging out", {
          hideProgressBar: true,
        });
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      !logoutDropdownArrowRef.current?.contains(event.target as Node)
    ) {
      setDropdownOpen(false);
    }
    if (
      notificationRef.current &&
      !notificationRef.current.contains(event.target as Node) &&
      !bellIconRef.current?.contains(event.target as Node)
    ) {
      setNotificationOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);
  useEffect(() => {
    setUnreadNotifications(
      notifications.filter(notification => !notification.isRead)
    );
  }, [notifications]);
  return (
    <div className="flex justify-end items-center p-4 bg-white border-b border-l border-gray-200 mt-[3px] top-0">
      <div className="flex items-center space-x-4">
        {showPlatformNotifications && (
          <div
            className="relative bg-inactive p-2 rounded-xl cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              setNotificationOpen(!isNotificationOpen);
            }}
            ref={bellIconRef}
          >
            <BellIcon className="w-6 h-6" />
            {unreadNotifications.length > 0 && (
              <div className="absolute -top-1 -right-1 bg-[#673148] text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {unreadNotifications.length}
              </div>
            )}

            {isNotificationOpen && (
              <div className="absolute top-10 left-1 w-full h-full">
                <Triangle />
              </div>
            )}
          </div>
        )}
        {showImageGeneratorBell && imageJobs && (
          <div
            className="relative bg-inactive p-2 rounded-xl cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              setNotificationOpen(!isNotificationOpen);
            }}
            ref={bellIconRef}
          >
            <BellIcon className="w-6 h-6" />
            {generationUnread > 0 && (
              <div className="absolute -top-1 -right-1 bg-[#673148] text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {generationUnread > 9 ? "9+" : generationUnread}
              </div>
            )}
            {isNotificationOpen && (
              <div className="absolute top-10 left-1 w-full h-full">
                <Triangle />
              </div>
            )}
          </div>
        )}
        <div className="flex items-center pl-4">
          {isClientUser && (
            <button
              type="button"
              className="flex items-center gap-2 text-sm font-medium text-secondary border border-light rounded-full h-10 px-4 mr-3 hover:bg-gray whitespace-nowrap"
              onClick={handleBackToPlatform}
            >
              <ReturnArrow className="w-3.5 h-3" />
              Back to platform
            </button>
          )}
          <div className="flex items-center space-x-2">
            <div className="bg-active text-center text-black font-bold rounded-xl w-10 h-10 flex items-center justify-center">
              {getUserInitials(user?.name)}
            </div>
            <div className="text-left pl-2">
              <div className="text-black font-medium">
                {stringShortener(capitalizeFirstLetter(name || ""), 10) || "NA"}
              </div>
              <div className="text-gray-500 text-sm">
                {group ? capitalizeFirstLetter(group) : "NA"}
              </div>
            </div>
            <div ref={logoutDropdownArrowRef}>
              <ArrowIcon
                className={`w-4 h-4 transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : ""}`}
                onClick={() => {
                  setDropdownOpen(!isDropdownOpen);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-20 right-4 top-16 bg-white border border-gray-200 rounded-lg shadow-lg w-36 p-2 transition-opacity duration-300 opacity-100"
        >
          <button
            className="bg-red-500 text-white font-bold text-sm py-2 rounded-full w-full focus:outline-none hover:bg-red-600"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      )}
      {showPlatformNotifications && isNotificationOpen && (
        <div
          className="absolute z-100 right-20 top-20 bg-white border border-gray-200 rounded-lg shadow-lg w-64 transition-opacity duration-300 opacity-100"
          ref={notificationRef}
        >
          <NotificationModal notifications={notifications} />
        </div>
      )}
      {showImageGeneratorBell && isNotificationOpen && (
        <div
          className="absolute z-100 right-20 top-20 bg-white border border-gray-200 rounded-lg shadow-lg w-80 overflow-hidden"
          ref={notificationRef}
        >
          <div className="flex items-center justify-between border-b border-light px-4 py-3">
            <p className="text-sm font-semibold text-secondary">Generations</p>
            {generationNotifications.length > 0 && (
              <button
                type="button"
                className="text-xs text-tertiary hover:text-secondary"
                onClick={() => {
                  imageJobs?.markAllRead();
                  collectionJobs?.markAllRead();
                }}
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {generationNotifications.length === 0 ? (
              <p className="px-4 py-6 text-xs text-tertiary">
                We’ll notify you here when a look or collection is ready.
              </p>
            ) : (
              generationNotifications.map(item => (
                <button
                  key={`${item.source}-${(item.source === "image" ? item.kind : undefined) || "look"}-${item.id}-${item.at}`}
                  type="button"
                  className={`block w-full border-b border-light px-4 py-3 text-left last:border-b-0 hover:bg-gray ${
                    item.read ? "bg-white" : "bg-[#F5E6E0]/40"
                  }`}
                  onClick={() => {
                    if (item.source === "collection") {
                      collectionJobs?.openSession(item.id);
                    } else {
                      imageJobs?.openSession(item.id);
                    }
                    setNotificationOpen(false);
                  }}
                >
                  <span className="block truncate text-xs font-medium text-secondary">
                    {item.title}
                  </span>
                  <span
                    className={`mt-0.5 block text-[11px] ${
                      item.status === "failed"
                        ? "text-red-600"
                        : "text-tertiary"
                    }`}
                  >
                    {item.status === "generated"
                      ? "Ready to view"
                      : item.errorMessage || "Generation failed"}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TopNav;
